import React, { useEffect, useState } from 'react';
import { FileText, BookOpen, Layers, Check, X } from 'lucide-react';
import { Button } from '../../../shared/components/Button';
import { listAllProjects, loadProject, ProjectSummary } from '../../../core/persistence/projectRepository';
import { DocumentModel } from '../../../core/ast/document.types';

interface ImportSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectDoc: (doc: DocumentModel) => void;
}

export const ImportSourceModal: React.FC<ImportSourceModalProps> = ({
  isOpen,
  onClose,
  onSelectDoc,
}) => {
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      listAllProjects()
        .then((items) => {
          setProjects(items);
          if (items.length > 0) setSelectedId(items[0].id);
        })
        .finally(() => setLoading(false));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (!selectedId) return;
    const doc = await loadProject(selectedId);
    if (doc) {
      onSelectDoc(doc);
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.5)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-4)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-xl)',
          width: '100%',
          maxWidth: '560px',
          maxHeight: '80vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: 'var(--space-4) var(--space-6)',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <Layers size={18} color="var(--primary-600)" />
            <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 600 }}>
              Insert Saved Document as Pages
            </h3>
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 'var(--space-6)', overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--text-muted)' }}>
              Loading saved documents...
            </div>
          ) : projects.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--text-muted)' }}>
              <FileText size={32} style={{ margin: '0 auto var(--space-2)', opacity: 0.5 }} />
              <p>No saved documents found.</p>
              <p style={{ fontSize: 'var(--font-size-xs)' }}>Create and save a Cover Page or Lab Report first.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {projects.map((proj) => {
                const isSelected = selectedId === proj.id;
                return (
                  <div
                    key={proj.id}
                    onClick={() => setSelectedId(proj.id)}
                    style={{
                      padding: 'var(--space-3) var(--space-4)',
                      border: isSelected ? '2px solid var(--primary-500)' : '1px solid var(--border-default)',
                      backgroundColor: isSelected ? 'var(--primary-50)' : 'var(--bg-canvas)',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                      {proj.isCoverOnly ? (
                        <FileText size={20} color="var(--primary-600)" />
                      ) : (
                        <BookOpen size={20} color="var(--color-info)" />
                      )}
                      <div>
                        <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>{proj.title}</div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                          {proj.documentType.replace('_', ' ').toUpperCase()} • {proj.sectionCount} sections • {new Date(proj.modifiedAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>

                    {isSelected && (
                      <div
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: 'var(--primary-600)',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Check size={12} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: 'var(--space-4) var(--space-6)',
            borderTop: '1px solid var(--border-subtle)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--space-3)',
          }}
        >
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!selectedId || projects.length === 0} onClick={handleConfirm}>
            Insert Document Pages
          </Button>
        </div>
      </div>
    </div>
  );
};
